import { formatCurrency } from './currency';

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const scopedKey = (userId, name) => `righand:${userId || 'demo'}:${name}`;

export const DEFAULT_DRIVER_TARGETS = {
  targetRatePerMile: 2000,
  targetWeeklyProfit: 1500000,
  fuelReserveMiles: 300,
  tankGallons: 450,
  fuelLevelPct: '',
  maintenanceWarningMiles: 1000,
};

// Starting goals in each country's own currency; drivers can change the weekly goal in the app.
const CURRENCY_GOALS = {
  UGX: { targetRatePerMile: 2000, targetWeeklyProfit: 1500000 },
  KES: { targetRatePerMile: 60, targetWeeklyProfit: 50000 },
  RWF: { targetRatePerMile: 500, targetWeeklyProfit: 450000 },
  EUR: { targetRatePerMile: 0.6, targetWeeklyProfit: 400 },
  USD: { targetRatePerMile: 0.5, targetWeeklyProfit: 400 },
};

export const getEffectiveTargets = (targets, currencyCode) => {
  const goals = CURRENCY_GOALS[currencyCode] || CURRENCY_GOALS.USD;
  const customWeekly = Number(targets.customWeeklyProfit);
  return {
    ...targets,
    targetRatePerMile: goals.targetRatePerMile,
    targetWeeklyProfit: customWeekly > 0 ? customWeekly : goals.targetWeeklyProfit,
  };
};

export const DriverOpsStore = {
  getLoadPackets(userId) {
    return readJson(scopedKey(userId, 'loadPackets'), []);
  },

  saveLoadPackets(userId, packets) {
    writeJson(scopedKey(userId, 'loadPackets'), packets);
  },

  getMaintenance(userId) {
    return readJson(scopedKey(userId, 'maintenance'), []);
  },

  saveMaintenance(userId, items) {
    writeJson(scopedKey(userId, 'maintenance'), items);
  },

  getTargets(userId) {
    return {
      ...DEFAULT_DRIVER_TARGETS,
      ...readJson(scopedKey(userId, 'targets'), {}),
    };
  },

  saveTargets(userId, targets) {
    writeJson(scopedKey(userId, 'targets'), targets);
  },
};

export const emptyLoadPacket = () => ({
  id: '',
  status: 'planned',
  loadNumber: '',
  broker: '',
  shipper: '',
  receiver: '',
  pickupDate: '',
  deliveryDate: '',
  rate: '',
  loadedMiles: '',
  deadheadMiles: '',
  fuelEstimate: '',
  tolls: '',
  detentionTerms: '',
  lumper: '',
  pickupAddress: '',
  deliveryAddress: '',
  notes: '',
  contractUrl: '',
  bolUrl: '',
  podUrl: '',
});

export const emptyMaintenanceItem = () => ({
  id: '',
  name: 'Oil service',
  dueOdometer: '',
  dueDate: '',
  lastCompletedOdometer: '',
  notes: '',
});

export const computeLoadDecision = (packet, targets = DEFAULT_DRIVER_TARGETS) => {
  const rate = Number(packet.rate) || 0;
  const loaded = Number(packet.loadedMiles) || 0;
  const deadhead = Number(packet.deadheadMiles) || 0;
  const fuel = Number(packet.fuelEstimate) || 0;
  const tolls = Number(packet.tolls) || 0;
  const lumper = Number(packet.lumper) || 0;
  const totalMiles = loaded + deadhead;
  const net = rate - fuel - tolls - lumper;
  const ratePerMile = totalMiles > 0 ? rate / totalMiles : null;
  const netPerMile = totalMiles > 0 ? net / totalMiles : null;
  const target = Number(targets.targetRatePerMile) || DEFAULT_DRIVER_TARGETS.targetRatePerMile;

  let score = 'Add distance';
  if (netPerMile !== null) {
    if (netPerMile >= target) score = 'Take';
    else if (netPerMile >= target * 0.85) score = 'Review';
    else score = 'Pass';
  }

  return { totalMiles, net, ratePerMile, netPerMile, score, target };
};

export const latestOdometer = (expenses) => expenses
  .map(entry => Number(entry.odometer) || 0)
  .filter(Boolean)
  .sort((a, b) => b - a)[0] || null;

const maintenanceOdometerAlert = (item, odometer, warningMiles, distanceUnit) => {
  const dueOdo = Number(item.dueOdometer) || 0;
  if (!odometer || !dueOdo) return null;

  const milesLeft = dueOdo - odometer;
  if (milesLeft <= 0) {
    return {
      type: 'maintenance',
      level: 'danger',
      title: `${item.name} overdue`,
      body: `${item.name} is overdue by ${Math.abs(milesLeft).toFixed(0)} ${distanceUnit}.`,
    };
  }
  if (milesLeft <= warningMiles) {
    return {
      type: 'maintenance',
      level: 'warning',
      title: `${item.name} due soon`,
      body: `${item.name} due in ${milesLeft.toFixed(0)} ${distanceUnit}.`,
    };
  }
  return null;
};

const maintenanceDateAlert = (item) => {
  if (!item.dueDate) return null;

  const daysLeft = Math.ceil((new Date(item.dueDate).getTime() - Date.now()) / 86400000);
  if (daysLeft > 14) return null;

  const dateStatus = daysLeft < 0 ? 'overdue' : 'coming up';
  const dateBody = daysLeft < 0
    ? `${Math.abs(daysLeft)} days overdue`
    : `due in ${daysLeft} days`;
  return {
    type: 'maintenance',
    level: daysLeft < 0 ? 'danger' : 'warning',
    title: `${item.name} date ${dateStatus}`,
    body: `${item.name} is ${dateBody}.`,
  };
};

const collectMaintenanceAlerts = (maintenanceItems, odometer, warningMiles, distanceUnit) => (
  maintenanceItems.flatMap(item => [
    maintenanceOdometerAlert(item, odometer, warningMiles, distanceUnit),
    maintenanceDateAlert(item),
  ].filter(Boolean))
);

const collectLoadAlerts = (loadPackets, targets, formatMoney, distanceUnit) => {
  const followUpAlerts = loadPackets
    .filter(packet => packet.status !== 'paid' && packet.deliveryDate)
    .filter(packet => Math.floor((Date.now() - new Date(packet.deliveryDate).getTime()) / 86400000) >= 5)
    .map(packet => {
      const daysSinceDelivery = Math.floor((Date.now() - new Date(packet.deliveryDate).getTime()) / 86400000);
      return {
        type: 'load',
        level: 'warning',
        title: 'Payment follow-up',
        body: `${packet.broker || 'Your freight agent'} has not paid yet: ${daysSinceDelivery} days since delivery of order ${packet.loadNumber || packet.shipper || 'trip file'}.`,
      };
    });

  const packetAlerts = loadPackets.flatMap(packet => {
    const alerts = [];
    const decision = computeLoadDecision(packet, targets);
    if (decision.netPerMile !== null && decision.netPerMile < decision.target) {
      alerts.push({
        type: 'load',
        level: decision.score === 'Pass' ? 'danger' : 'warning',
        title: `Load pays ${formatMoney(decision.netPerMile)}/${distanceUnit}`,
        body: `Your target is ${formatMoney(decision.target)}/${distanceUnit} after fuel, tolls, empty-run distance and loading labour.`,
      });
    }
    if (packet.pickupDate && packet.deliveryDate && new Date(packet.deliveryDate) < new Date(packet.pickupDate)) {
      alerts.push({
        type: 'load',
        level: 'danger',
        title: 'Delivery before pickup',
        body: 'Check the pickup and delivery dates on the trip file.',
      });
    }
    return alerts;
  });

  return [...followUpAlerts, ...packetAlerts];
};

const DEFAULT_UNITS = { fuelUnit: 'litres', distanceUnit: 'km', efficiencyUnit: 'km/L', gapWarning: 880 };

export const analyzeDriverOps = ({ expenses, loadPackets, maintenanceItems, targets, weeklyDays, formatMoney = (value) => formatCurrency(value, 'USD'), units = DEFAULT_UNITS }) => {
  const alerts = [];
  const fuelEntries = expenses
    .filter(e => e.type === 'expense' && e.category === 'fuel')
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const odometer = latestOdometer(expenses);

  fuelEntries.slice(0, 8).forEach(entry => {
    if ((Number(entry.gallons) || 0) > 0 && !entry.fuelState) {
      alerts.push({
        type: 'ifta',
        level: 'warning',
        title: 'Fuel country missing',
        body: `You entered ${Number(entry.gallons).toFixed(1)} ${units.fuelUnit} without a fuel country. Add the country for cross-border fuel records.`,
      });
    }
    if ((Number(entry.gallons) || 0) <= 0) {
      alerts.push({
        type: 'ifta',
        level: 'warning',
        title: `Fuel ${units.fuelUnit} missing`,
        body: `Fuel entries need ${units.fuelUnit} for fuel records and ${units.efficiencyUnit} tracking.`,
      });
    }
  });

  const sortedFuelWithOdo = fuelEntries
    .filter(e => Number(e.odometer) > 0)
    .sort((a, b) => Number(b.odometer) - Number(a.odometer));
  if (sortedFuelWithOdo.length >= 2) {
    const gap = Number(sortedFuelWithOdo[0].odometer) - Number(sortedFuelWithOdo[1].odometer);
    if (gap > units.gapWarning) {
      alerts.push({
        type: 'fuel',
        level: 'warning',
        title: 'Fuel log may be missing',
        body: `You drove ${gap.toFixed(0)} ${units.distanceUnit} between fuel odometers. Check for a missing fuel stop.`,
      });
    }
  }

  const mpgReadings = sortedFuelWithOdo
    .map((entry, index, list) => {
      const previous = list[index + 1];
      if (!previous || !entry.gallons) return null;
      const miles = Number(entry.odometer) - Number(previous.odometer);
      const gallons = Number(entry.gallons);
      return miles > 0 && gallons > 0 ? miles / gallons : null;
    })
    .filter(Boolean);
  if (mpgReadings.length >= 3) {
    const latest = mpgReadings[0];
    const average = mpgReadings.slice(1, 4).reduce((sum, mpg) => sum + mpg, 0) / Math.min(3, mpgReadings.length - 1);
    if (latest < average * 0.82) {
      alerts.push({
        type: 'fuel',
        level: 'warning',
        title: `${units.efficiencyUnit} dropped`,
        body: `Fuel efficiency dropped from ${average.toFixed(1)} to ${latest.toFixed(1)} ${units.efficiencyUnit}. Check idle time, load weight, tyres, or route.`,
      });
    }
  }

  if (mpgReadings.length && targets.fuelLevelPct !== '' && Number(targets.tankGallons) > 0) {
    const estimatedRange = (Number(targets.tankGallons) * (Number(targets.fuelLevelPct) / 100)) * mpgReadings[0];
    if (estimatedRange <= targets.fuelReserveMiles) {
      alerts.push({
        type: 'fuel',
        level: 'warning',
        title: 'Fuel range getting low',
        body: `At current ${units.efficiencyUnit} and fuel level, estimated range is ${estimatedRange.toFixed(0)} ${units.distanceUnit}.`,
      });
    }
  }

  const weeklyProfit = weeklyDays.reduce((sum, day) => sum + (Number(day.net) || 0), 0);
  if (weeklyProfit < targets.targetWeeklyProfit) {
    alerts.push({
      type: 'profit',
      level: 'info',
      title: 'Weekly profit goal',
      body: `You have made ${formatMoney(weeklyProfit)} this week. Your goal is ${formatMoney(targets.targetWeeklyProfit)}.`,
    });
  }

  return [
    ...alerts,
    ...collectMaintenanceAlerts(maintenanceItems, odometer, targets.maintenanceWarningMiles, units.distanceUnit),
    ...collectLoadAlerts(loadPackets, targets, formatMoney, units.distanceUnit),
  ].slice(0, 8);
};
